import { useInfiniteQuery, useQuery } from '@tanstack/react-query'

import { fetch, generateGuid } from '@/shared/api/fetcher'
import { captureRefundTermsError, captureTermsError } from '@/shared/sentry/validation'
import {
  FaqListRequestType,
  FaqListResponseType,
  NewsRequestType,
  NewsResponseType,
  NoticeAndNewsRequestType,
  NoticeAndNewsResponseType,
  NoticeItemRequestType,
  NoticeItemResponseType,
  NoticeItemType,
  NoticeListRequestType,
  NoticeListResponseType,
  PaginationRequestType,
  PopupType,
  RefundTermsResponseType,
  TermsResponseType,
} from '@/shared/types/api'
import { CMDTYPE } from '@/shared/types/cmdType'
import { ERRORCODE } from '@/shared/types/errorCode'

const useHomeQuery = () => {
  const pagination: PaginationRequestType = {
    Length: 5,
    Offset: 0,
  }

  const { data, isLoading } = useQuery({
    queryKey: ['noticeAndNews', pagination],
    queryFn: () =>
      fetch.post<NoticeAndNewsRequestType, NoticeAndNewsResponseType>(
        {
          CmdType: CMDTYPE.NOTICE_NEWS_LIST,
          Browser: 'CHROME',
          LocalIP: '192.0.0.0',
          TransactionID: generateGuid(),
        },
        pagination,
      ),
  })

  const defaultPopupData: PopupType = {
    Idx: 0,
    PopupTitle: '',
    LinkPath: '',
    ImgPath: '',
    StartDT: '',
    EndDT: '',
    RegDT: '',
  }

  const parsedBody = data?.Body ?? {
    News: [],
    Notice: [],
    Popup: defaultPopupData,
  }

  return {
    newsData: parsedBody.News,
    noticeData: parsedBody.Notice,
    popUpData: parsedBody.Popup,
    isLoading,
  }
}

const useNewsListQuery = () => {
  const { data, isLoading, fetchNextPage, hasNextPage } = useInfiniteQuery({
    queryKey: ['newsList'],
    initialPageParam: 0,
    queryFn: async ({ pageParam = 0 }) => {
      const PAGE_SIZE = 6
      const requestData: NewsRequestType = {
        Length: PAGE_SIZE + 1,
        Offset: pageParam * PAGE_SIZE,
      }

      const result = await fetch.post<NewsRequestType, NewsResponseType>(
        {
          CmdType: CMDTYPE.NEWS_LIST,
          Browser: 'CHROME',
          LocalIP: '192.0.0.0',
          RequestID: generateGuid(),
        },
        requestData,
      )

      const rawData = result.Body ?? []
      const hasNext = rawData.length > PAGE_SIZE

      return {
        items: rawData.slice(0, PAGE_SIZE),
        nextPage: hasNext ? pageParam + 1 : undefined,
      }
    },
    getNextPageParam: (lastPage) => lastPage.nextPage,
  })

  const newsListData = data?.pages.flatMap((page) => page.items) ?? []

  return { newsListData, isLoading, fetchNextPage, hasNextPage }
}

const useNoticeListQuery = () => {
  const { data, isLoading, fetchNextPage, hasNextPage } = useInfiniteQuery({
    queryKey: ['noticeList'],
    initialPageParam: 0,
    queryFn: async ({ pageParam = 0 }) => {
      const NOTICE_PAGESIZE = 6

      const requestData: NoticeListRequestType = {
        Length: NOTICE_PAGESIZE + 1,
        Offset: (pageParam as number) * NOTICE_PAGESIZE,
      }

      const result = await fetch.post<NoticeListRequestType, NoticeListResponseType>(
        {
          CmdType: CMDTYPE.NOTICE_LIST,
          Browser: 'CHROME',
          LocalIP: '192.0.0.0',
          TransactionID: generateGuid(),
        },
        requestData,
      )

      const rawData = result.Body ?? []
      const hasNext = rawData.length > NOTICE_PAGESIZE

      return {
        items: rawData.slice(0, NOTICE_PAGESIZE),
        nextPage: hasNext ? pageParam + 1 : undefined,
      }
    },
    getNextPageParam: (lastPage) => lastPage.nextPage,
  })
  const noticeListData = data?.pages.flatMap((page) => page.items) ?? []

  return {
    noticeListData,
    isLoading,
    fetchNextPage,
    hasNextPage,
  }
}

const useNoticeItemQuery = (idx: number) => {
  const requestData = { Idx: idx }

  const { data, isLoading } = useQuery({
    queryKey: ['noticeDetail', requestData],
    enabled: !!idx,
    queryFn: () =>
      fetch.post<NoticeItemRequestType, NoticeItemResponseType>(
        { CmdType: CMDTYPE.NOTICE_ITEM, CallApp: 'www', TransactionID: generateGuid() },
        requestData,
      ),
  })
  const noticeDetailData: NoticeItemType =
    Array.isArray(data?.Body) && data.Body.length > 0 && data.Body[0]

  return { noticeDetailData, isLoading }
}

const useFaqListQuery = (group: number) => {
  const { data, isLoading, fetchNextPage, hasNextPage } = useInfiniteQuery({
    queryKey: ['faqList', group],
    initialPageParam: 0,
    queryFn: async ({ pageParam = 0 }) => {
      const PAGE_SIZE = 5

      const requestData: FaqListRequestType = {
        Length: PAGE_SIZE + 1,
        Offset: pageParam * PAGE_SIZE,
        Group: group,
      }

      const result = await fetch.post<FaqListRequestType, FaqListResponseType>(
        {
          CmdType: CMDTYPE.FAQ_LIST,
          CallApp: 'www',
          TransactionID: generateGuid(),
        },
        requestData,
      )

      const rawData = result.Body ?? []
      const hasNext = rawData.length > PAGE_SIZE

      return {
        items: rawData.slice(0, PAGE_SIZE),
        nextPage: hasNext ? pageParam + 1 : undefined,
      }
    },
    getNextPageParam: (lastPage) => lastPage.nextPage,
  })

  const faqListData = data?.pages.flatMap((page) => page.items) ?? []

  return { faqListData, isLoading, fetchNextPage, hasNextPage }
}

const useTermsQuery = (telecom: string | undefined, termsIdx: string | undefined) => {
  const { data, isLoading } = useQuery({
    queryKey: ['terms'],
    queryFn: async () => {
      const result = await fetch.post<unknown, TermsResponseType>(
        { CmdType: CMDTYPE.TERMS_LIST, CallApp: 'www', RequestID: generateGuid() },
        {},
      )

      if (result.Header.ResultCd === ERRORCODE.SUCCESS) {
        captureTermsError(result.Body)
      }
      return result
    },
    enabled: !!telecom && !!termsIdx,
  })

  const terms = data?.Body ? [data.Body.SKTTerms, data.Body.KTTerms, data.Body.LGUPTerms] : []
  const telecomIndex = telecom === '10' ? 0 : telecom === '20' ? 1 : telecom === '30' ? 2 : -1

  const selectedTerm = terms[telecomIndex]?.Terms?.[Number(termsIdx) - 1]?.Contents ?? ''

  return {
    selectedTerm,
    isLoading,
  }
}

const useRefundTermsQuery = () => {
  const { data, isLoading } = useQuery({
    queryKey: ['refundTerms'],
    queryFn: async () => {
      const result = await fetch.post<unknown, RefundTermsResponseType>(
        {
          CmdType: CMDTYPE.REFUND_TERMS,
          RequestID: generateGuid(),
        },
        {},
      )
      if (result.Header.ResultCd === ERRORCODE.SUCCESS) {
        captureRefundTermsError(result.Body)
      }
      return result
    },
  })
  return { refundTerms: data?.Body, isLoading }
}

export {
  useFaqListQuery,
  useHomeQuery,
  useNewsListQuery,
  useNoticeItemQuery,
  useNoticeListQuery,
  useRefundTermsQuery,
  useTermsQuery,
}
